import React, { useEffect, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import type { CatalogFlowCourseOption } from '../../utils/catalogFlowCourse';
import { CatalogFlowCourseScopeStrip } from './CatalogFlowCourseScopeStrip';
import { catalogFlowFilterRowClass, catalogFlowPillTrackClass } from './catalogFlowFilterStyles';

export type CatalogFlowFilterMode = 'quiz' | 'exercise';

type FilterKey = 'all' | 'done' | 'remaining';

type Props = {
  mode: CatalogFlowFilterMode;
  filter: FilterKey;
  onFilterChange: (f: FilterKey) => void;
  countAll: number;
  countDone: number;
  countRemaining: number;
  /** When false, filter row is hidden (no items). */
  show: boolean;
  /**
   * When false (quiz / exercise flow pages), the filter row is always visible and the icon is not a toggle.
   * Tag content page uses its own filter UI — it does not use this component.
   * @default true
   */
  collapsible?: boolean;
  /** Second filter row: course scope (AND with status filter). null = all courses. */
  courseOptions?: CatalogFlowCourseOption[];
  courseFilter?: number | null;
  onCourseFilterChange?: (courseId: number | null) => void;
};

const FilterIconBlock: React.FC<{ active?: boolean }> = ({ active }) => (
  <div
    className={`mt-0.5 flex-shrink-0 rounded-lg p-2 sm:mt-0 ${
      active ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-100 dark:bg-gray-800'
    }`}
    aria-hidden
  >
    <Filter className="h-5 w-5 text-gray-600 dark:text-gray-300" />
  </div>
);

/**
 * Filter row with sliding pill indicator (quiz / exercise catalog flows).
 * Use `collapsible={false}` for a fixed bar; `collapsible={true}` keeps expand/collapse (optional; tag page has its own UI).
 */
export const CatalogFlowFilterCollapsible: React.FC<Props> = ({
  mode,
  filter,
  onFilterChange,
  countAll,
  countDone,
  countRemaining,
  show,
  collapsible = true,
  courseOptions = [],
  courseFilter = null,
  onCourseFilterChange
}) => {
  const { t } = useTranslation('learning');
  const { isDark } = useTheme();
  const [filterOpen, setFilterOpen] = useState(true);
  const filterContentRef = useRef<HTMLDivElement>(null);
  const filterPillContainerRef = useRef<HTMLDivElement>(null);
  const allPillRef = useRef<HTMLButtonElement>(null);
  const donePillRef = useRef<HTMLButtonElement>(null);
  const remainingPillRef = useRef<HTMLButtonElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  useEffect(() => {
    const activeCount = filter === 'all' ? countAll : filter === 'done' ? countDone : countRemaining;
    if (activeCount > 0 || filter === 'all' || countAll === 0) return;
    onFilterChange('all');
  }, [filter, countAll, countDone, countRemaining, onFilterChange]);

  const expanded = !collapsible || filterOpen;

  useEffect(() => {
    if (!expanded) return;
    const container = filterPillContainerRef.current;
    const active =
      filter === 'all' ? allPillRef.current : filter === 'done' ? donePillRef.current : remainingPillRef.current;
    if (!container || !active) return;
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    setIndicatorStyle({
      left: aRect.left - cRect.left,
      width: aRect.width
    });
  }, [expanded, filter, countAll, countDone, countRemaining]);

  useEffect(() => {
    if (!expanded) return;
    const t = window.setTimeout(() => {
      const container = filterPillContainerRef.current;
      const active =
        filter === 'all' ? allPillRef.current : filter === 'done' ? donePillRef.current : remainingPillRef.current;
      if (!container || !active) return;
      const cRect = container.getBoundingClientRect();
      const aRect = active.getBoundingClientRect();
      setIndicatorStyle({ left: aRect.left - cRect.left, width: aRect.width });
    }, 380);
    return () => clearTimeout(t);
  }, [expanded, filter]);

  const remainingIndicator =
    mode === 'exercise'
      ? isDark
        ? 'rgb(120 113 108)'
        : 'rgb(168 162 158)'
      : isDark
        ? 'rgb(251 191 36)'
        : 'rgb(245 158 11)';

  const indicatorColors: Record<FilterKey, string> = {
    all: isDark ? 'rgb(229 231 235)' : 'rgb(31 41 55)',
    /** Slightly lighter emerald in dark mode so “Passed / Reviewed” reads clearly on the bar. */
    done: isDark ? 'rgb(52 211 153)' : 'rgb(5 150 105)',
    remaining: remainingIndicator
  };

  const doneLabel = mode === 'quiz' ? t('catalogFlow.filter.passed') : t('catalogFlow.filter.reviewed');

  const showCourseRow = courseOptions.length > 0 && onCourseFilterChange != null;

  if (!show) return null;

  const statusPillsRow = (
    <div className={catalogFlowFilterRowClass}>
      <div ref={filterPillContainerRef} className={catalogFlowPillTrackClass}>
        <div
          className="pointer-events-none absolute bottom-1 top-1 rounded-full shadow-sm"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
            backgroundColor: indicatorColors[filter],
            transition:
              'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
        <button
          ref={allPillRef}
          type="button"
          disabled={countAll === 0}
          onClick={() => onFilterChange('all')}
          className={`relative z-10 inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium transition-colors duration-300 sm:px-3 sm:py-1.5 sm:text-sm disabled:cursor-not-allowed disabled:opacity-40 ${
            filter === 'all' ? 'text-white dark:text-gray-900' : 'text-gray-600 hover:text-gray-900 disabled:hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-100 dark:disabled:hover:text-gray-400'
          }`}
        >
          {t('catalogFlow.filter.all')}
          <span
            className={`min-w-[1.125rem] rounded-full px-1 py-0.5 text-[10px] tabular-nums transition-colors duration-300 sm:min-w-[1.25rem] sm:text-xs ${
              filter === 'all' ? 'bg-white/25 dark:bg-black/20' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            {countAll}
          </span>
        </button>
        <button
          ref={donePillRef}
          type="button"
          disabled={countDone === 0}
          onClick={() => onFilterChange('done')}
          className={`relative z-10 inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium transition-colors duration-300 sm:px-3 sm:py-1.5 sm:text-sm disabled:cursor-not-allowed disabled:opacity-40 ${
            filter === 'done'
              ? 'text-white dark:text-gray-900'
              : 'text-gray-600 hover:text-emerald-800 disabled:hover:text-gray-600 dark:text-gray-400 dark:hover:text-emerald-200 dark:disabled:hover:text-gray-400'
          }`}
        >
          {doneLabel}
          <span
            className={`min-w-[1.125rem] rounded-full px-1 py-0.5 text-[10px] tabular-nums transition-colors duration-300 sm:min-w-[1.25rem] sm:text-xs ${
              filter === 'done'
                ? 'bg-white/25 dark:bg-gray-900/12'
                : 'bg-emerald-200/50 text-emerald-800 dark:bg-emerald-800/40 dark:text-emerald-200'
            }`}
          >
            {countDone}
          </span>
        </button>
        <button
          ref={remainingPillRef}
          type="button"
          disabled={countRemaining === 0}
          onClick={() => onFilterChange('remaining')}
          className={`relative z-10 inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium transition-colors duration-300 sm:px-3 sm:py-1.5 sm:text-sm disabled:cursor-not-allowed disabled:opacity-40 ${
            filter === 'remaining' ? 'text-gray-900 dark:text-gray-900' : mode === 'exercise'
              ? 'text-gray-600 hover:text-stone-800 disabled:hover:text-gray-600 dark:text-gray-400 dark:hover:text-stone-200 dark:disabled:hover:text-gray-400'
              : 'text-gray-600 hover:text-amber-800 disabled:hover:text-gray-600 dark:text-gray-400 dark:hover:text-amber-200 dark:disabled:hover:text-gray-400'
          }`}
        >
          <span className="hidden sm:inline">{t('catalogFlow.filter.remaining')}</span>
          <span className="sm:hidden">{t('catalogFlow.filter.remainingShort')}</span>
          <span
            className={`min-w-[1.125rem] rounded-full px-1 py-0.5 text-[10px] tabular-nums transition-colors duration-300 sm:min-w-[1.25rem] sm:text-xs ${
              filter === 'remaining'
                ? 'bg-black/15 dark:bg-black/25'
                : mode === 'exercise'
                  ? 'bg-stone-200/60 text-stone-800 dark:bg-stone-700/45 dark:text-stone-200'
                  : 'bg-amber-200/50 text-amber-800 dark:bg-amber-800/40 dark:text-amber-200'
            }`}
          >
            {countRemaining}
          </span>
        </button>
      </div>
    </div>
  );

  const filterRows = (
    <div className="flex w-full min-w-0 flex-col gap-1">
      {statusPillsRow}
      {showCourseRow && (
        <CatalogFlowCourseScopeStrip
          mode={mode}
          courseOptions={courseOptions}
          courseFilter={courseFilter}
          onCourseFilterChange={onCourseFilterChange}
        />
      )}
    </div>
  );

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 shadow-sm dark:border-gray-700 dark:bg-gray-800/90">
      <div className="p-3 sm:p-4">
        {!collapsible ? (
          <div className="flex items-start gap-2 sm:items-center sm:gap-3">
            <FilterIconBlock active />
            <div className="min-w-0 flex-1">{filterRows}</div>
          </div>
        ) : (
          <div className="flex items-start gap-2 sm:items-center sm:gap-3">
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className={`mt-0.5 flex-shrink-0 rounded-lg p-2 transition-colors sm:mt-0 ${
                filterOpen ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700'
              }`}
              aria-label={filterOpen ? t('catalogFlow.filter.collapse') : t('catalogFlow.filter.expand')}
              aria-expanded={filterOpen}
            >
              <Filter className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>

            <div className="min-w-0 flex-1">
              <div
                className="grid transition-[grid-template-rows] duration-200 ease-out"
                style={{ gridTemplateRows: filterOpen ? '1fr' : '0fr' }}
              >
                <div ref={filterContentRef} className="overflow-hidden">
                  {filterRows}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
